/**
 * Loading, empty, forbidden, missing, failed — rendered the same way everywhere.
 *
 * The distinction that matters most is between *forbidden* and *failed*. A
 * platform that tells somebody "something went wrong" when the truth is "you may
 * not see this" has told them something false, and they will raise an incident
 * about it. So a refusal says it is a refusal, names nothing it should not, and
 * offers no retry — while a real failure offers one and shows the correlation
 * id, because that is what makes the request findable in the audit record.
 *
 * The second distinction is between *failed* and *unavailable*. Nexus reports a
 * dependency it could not reach with its own code — `DEPENDENCY_UNAVAILABLE`,
 * or `AUDIT_UNAVAILABLE` when the thing it could not reach was the audit sink —
 * and collapsing those into a generic failure sends somebody to debug their own
 * request when the platform has already said the fault is downstream of it and
 * the same request will work once the dependency returns. So it reads as a wait,
 * not as a defect, and the retry stays because retrying is exactly the right
 * response to a dependency coming back.
 */

import type { ReactNode } from "react";
import { NexusError } from "../api/errors";
import type { TransportError } from "../api/errors";
import type { Resource } from "../api/useResource";
import { CorrelationLink } from "./primitives";

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div className="nx-state" role="status" aria-live="polite">
      <span className="nx-visually-hidden">{label}</span>
      <div style={{ display: "grid", gap: 8, width: "min(360px, 100%)" }} aria-hidden="true">
        <div className="nx-skeleton" style={{ width: "60%" }} />
        <div className="nx-skeleton" />
        <div className="nx-skeleton" style={{ width: "80%" }} />
      </div>
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="nx-state">
      <p className="nx-state__title">{title}</p>
      {hint ? <p>{hint}</p> : null}
    </div>
  );
}

export function Forbidden({ what }: { what: string }) {
  return (
    <div className="nx-state" role="status">
      <p className="nx-state__title">You do not have access to {what}.</p>
      <p>
        Access is granted per operation, and reading is granted apart from changing. Ask whoever
        administers roles for this tenant.
      </p>
    </div>
  );
}

export function Missing({ what }: { what: string }) {
  return (
    <div className="nx-state" role="status">
      <p className="nx-state__title">No such {what}.</p>
      <p>It may have been retired, or it may never have existed in this deployment.</p>
    </div>
  );
}

/**
 * Codes that mean *Nexus could not reach something*, rather than *Nexus is
 * broken* or *your request was wrong*.
 *
 * Named from the platform's own taxonomy in `errors.ts` rather than inferred
 * from a status: a 503 is not always a dependency, and a dependency failure
 * does not always arrive as a 503.
 */
const UNAVAILABLE_CODES = new Set(["DEPENDENCY_UNAVAILABLE", "AUDIT_UNAVAILABLE"]);

export function isUnavailable(error: NexusError | TransportError): boolean {
  return error instanceof NexusError && UNAVAILABLE_CODES.has(error.code);
}

/**
 * Something Nexus depends on did not answer.
 *
 * Still `role="alert"`, like any other failure. The screen could not show what
 * it was asked for either way, and giving the two cases different urgency would
 * be an inconsistency with no reader benefit. What differs — and all that needs
 * to differ — is what it says: that the fault is downstream, that the request
 * was fine, and that the same request will work once the dependency returns.
 */
export function Unavailable({
  error,
  onRetry,
}: {
  error: NexusError | TransportError;
  onRetry?: () => void;
}) {
  const correlation = error instanceof NexusError ? error.correlationId : null;
  const code = error instanceof NexusError ? error.code : "DEPENDENCY_UNAVAILABLE";
  return (
    <div className="nx-state" role="alert">
      <p className="nx-state__title">Nexus could not reach something it needs.</p>
      <p>{error.message}</p>
      <p>
        This is not a refusal and there is nothing wrong with the request. Try again once the
        dependency is back.
      </p>
      <p className="nx-mono">{code}</p>
      {correlation ? (
        <p className="nx-hint">
          Correlation <CorrelationLink value={correlation} />
        </p>
      ) : null}
      {onRetry ? (
        <button type="button" className="nx-button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function Failed({
  error,
  onRetry,
}: {
  error: NexusError | TransportError;
  onRetry?: () => void;
}) {
  // Every screen already routes its failures through here, so making the
  // distinction at this one point is what gives all of them the same honest
  // answer without each having to remember to ask.
  if (isUnavailable(error)) return <Unavailable error={error} {...(onRetry ? { onRetry } : {})} />;

  const correlation = error instanceof NexusError ? error.correlationId : null;
  const code = error instanceof NexusError ? error.code : "UNREACHABLE";
  return (
    <div className="nx-state" role="alert">
      <p className="nx-state__title">{error.message}</p>
      <p className="nx-mono">{code}</p>
      {correlation ? (
        <p className="nx-hint">
          {/* The id is only useful if it leads somewhere. */}
          Correlation <CorrelationLink value={correlation} />
        </p>
      ) : null}
      {onRetry ? (
        <button type="button" className="nx-button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/**
 * Render a resource, handling every state it can be in.
 *
 * `what` names the thing being read, so a refusal can say what was refused
 * without the caller writing the sentence again.
 */
export function Show<T>({
  resource,
  what,
  onRetry,
  children,
  empty,
}: {
  resource: Resource<T>;
  what: string;
  onRetry?: () => void;
  children: (value: T) => ReactNode;
  empty?: (value: T) => ReactNode;
}) {
  switch (resource.kind) {
    case "loading":
      return <Loading label={`Loading ${what}`} />;
    case "forbidden":
      return <Forbidden what={what} />;
    case "missing":
      return <Missing what={what} />;
    case "failed":
      return <Failed error={resource.error} {...(onRetry ? { onRetry } : {})} />;
    case "ready": {
      const emptied = empty?.(resource.value);
      return <>{emptied ?? children(resource.value)}</>;
    }
  }
}
